import { Box, Typography, useTheme, useMediaQuery } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { MaskedCurrency } from '../MaskedValue';
import { resolveAmountColor } from '../AmountDisplay';
import { baselineNetCents, everythingCents, netIfSpentCents } from '../../utils/dashboardN';
import FigureNote from './FigureNote';

/**
 * The Month view card's Bottom line (#2139, epic #2138). Baseline net first
 * and always the larger figure (owner, #2207), then Net, all budgets spent on
 * the current month or Net at close on a closed one. Checking moved to the
 * top bar (#2206). The Ahead / Behind verdict pill and sentence retired with
 * #2179: the signed, colored figures say it. Every label-and-value pair
 * explains itself on hover after 2 seconds, on focus at once, and on tap on
 * a phone (FigureNote, #2179); no note text sits under a figure.
 *
 * From md up the two sit side by side, each centered in its cell, the second
 * right of a rule, above Money in and out (#2207). Below md every figure is
 * one row, label and value, in the same order.
 * Pointing at a figure lights its twin row in Money in and out, and the
 * reverse (#2207): `highlight` names the figure lit, `onHighlight` reports
 * pointer enter and leave.
 *
 * @param {object} props
 * @param {object} props.month               the `/flows` month entry
 * @param {boolean} props.isCurrent
 * @param {number} props.positiveBudgetCents Month to month positive balances (monthToMonthPositiveCents)
 * @param {string|null} [props.highlight]    the linked figure being pointed at
 * @param {(key: string|null) => void} [props.onHighlight]
 * @param {Object} props.strings             engine strings
 */
const BottomLine = ({ month, isCurrent, positiveBudgetCents, highlight = null, onHighlight, strings }) => {
  const theme = useTheme();
  const { palette, ds } = theme;
  const isWide = useMediaQuery(theme.breakpoints.up('md'));

  // Baseline net reads white or red at the board's 30 px; the Net figure is a
  // sum, green or red, at 26 px (section 11, #2207).
  const baseline = { key: 'baseline', cents: baselineNetCents(month), tone: 'row', sizePx: 30 };
  const figures = isCurrent
    ? [
      { ...baseline, note: strings['dashboardN.baseline.noteNow'] },
      { key: 'netIfSpent', cents: netIfSpentCents(month, positiveBudgetCents), note: strings['dashboardN.netIfSpent.note'], tone: 'total', sizePx: 26 },
    ]
    : [
      { ...baseline, note: strings['dashboardN.baseline.noteClosed'] },
      { key: 'everything', cents: everythingCents(month), note: strings['dashboardN.everything.note'], tone: 'total', sizePx: 26 },
    ];

  // One figure: the label-and-value pair, the hover target, centered in its
  // cell from md up.
  const figureCell = (f, sx = {}) => {
    const label = strings[`dashboardN.${f.key}`];
    const color = resolveAmountColor(palette, f.tone, f.cents < 0, f.cents === 0);
    return (
      <Box
        key={f.key}
        data-testid={`bottom-line-${f.key}`}
        data-highlight={highlight === f.key ? 'true' : undefined}
        onPointerEnter={onHighlight ? () => onHighlight(f.key) : undefined}
        onPointerLeave={onHighlight ? () => onHighlight(null) : undefined}
        sx={{
          minWidth: 0,
          py: `${ds.space[2]}px`,
          borderRadius: '6px',
          backgroundColor: highlight === f.key ? alpha(palette.interactive, 0.10) : 'transparent',
          transition: `background-color ${ds.motion.fast} ${ds.motion.easeOut}`,
          ...sx,
        }}
      >
        <FigureNote
          note={f.note}
          enterDelay={2000}
          sx={{
            display: 'flex',
            flexDirection: isWide ? 'column' : 'row',
            alignItems: isWide ? 'center' : 'baseline',
            textAlign: isWide ? 'center' : 'left',
            justifyContent: 'space-between',
            gap: isWide ? '2px' : `${ds.space[4]}px`,
            width: '100%',
          }}
        >
          <Typography
            component="span"
            sx={{
              fontSize: '11px',
              letterSpacing: ds.font.letterUpper,
              textTransform: 'uppercase',
              color: palette.text.dim,
              minWidth: 0,
            }}
          >
            {label}
          </Typography>
          <MaskedCurrency
            value={f.cents / 100}
            style={{
              fontSize: `${f.sizePx}px`,
              fontWeight: ds.font.weightMedium,
              letterSpacing: ds.font.letterHero,
              fontVariantNumeric: 'tabular-nums',
              lineHeight: 1.2,
              color,
              whiteSpace: 'nowrap',
            }}
          />
        </FigureNote>
      </Box>
    );
  };

  // From md up the second figure, the Net one, sits right of a rule.
  const right = { borderLeft: `1px solid ${palette.divider}`, pl: `${ds.space[8]}px` };
  const cells = figures.map((f, i) => figureCell(f, isWide && i === 1 ? right : {}));

  return (
    <Box
      data-testid="bottom-line"
      data-layout={isWide ? 'wide' : 'stacked'}
      sx={{
        display: 'grid',
        gridTemplateColumns: isWide ? 'repeat(2, minmax(0, 1fr))' : '1fr',
        columnGap: `${ds.space[8]}px`,
        rowGap: `${ds.space[2]}px`,
        py: `${ds.space[3]}px`,
      }}
    >
      {cells}
    </Box>
  );
};

export default BottomLine;
